import { ActionReducerMapBuilder, AsyncThunk } from '@reduxjs/toolkit';
import {
  DashboardMacroCategorySummary,
  DashboardWalletSummary,
  ProductMacroCategoryType,
} from '../../../model';
import { HomeState } from './home-types';

export const addHomeExtraReducers = <T, U, V, Z>(
  builder: ActionReducerMapBuilder<T>,
  thunk: AsyncThunk<U, V, Z>,
  key: keyof HomeState
) => {
  builder.addCase(thunk.pending, (state) => {
    state[key] = {
      requestStatus: 'loading',
    };
  });
  builder.addCase(thunk.rejected, (state, action) => {
    state[key] = {
      requestStatus: 'failed',
      error: action.payload || 'GENERIC_ERROR',
    };
  });
  builder.addCase(thunk.fulfilled, (state, action) => {
    state[key] = {
      requestStatus: 'succeeded',
      value: action?.payload,
    };
  });
};

const macroCategorySummaryOrderedTypes: {
  [key in ProductMacroCategoryType]: number;
} = {
  Managed: 0,
  RealEconomy: 1,
  FinancialInsurance: 2,
  Welfare: 3,
  TraditionalInsurance: 4,
  Administered: 5,
  Liquidity: 6,
  TotalFia: 7,
  TotalPortfolio: 8,
};

export const macroCategoryColorMap: {
  [key in ProductMacroCategoryType]: string;
} = {
  Managed: '#BAEC93',
  RealEconomy: '#65E4AF',
  FinancialInsurance: '#579C8D',
  Welfare: '#334CA3',
  TraditionalInsurance: '#372D76',
  Administered: '#A173AC',
  Liquidity: '#7E2E92',
  TotalFia: '#FFB547',
  TotalPortfolio: '#b90827',
};

export const sortMacroCategoriesByType = (
  macroCategories: DashboardMacroCategorySummary[]
): DashboardMacroCategorySummary[] => {
  const compareFn = (
    mca: DashboardMacroCategorySummary,
    mcb: DashboardMacroCategorySummary
  ): number =>
    macroCategorySummaryOrderedTypes[mca.type] -
    macroCategorySummaryOrderedTypes[mcb.type];

  return macroCategories ? [...macroCategories].sort(compareFn) : [];
};

export const dashboardEmptySummary: DashboardWalletSummary = {
  richiamiInAttesa: { value: undefined, currency: undefined },
  totalAmount: {
    value: 0,
    currency: 'EUR',
  },
  macroCategories: Object.keys(macroCategorySummaryOrderedTypes)
    .slice(0, 4)
    .map((type: ProductMacroCategoryType) => ({
      type,
      amount: {
        value: 0,
        currency: 'EUR',
      },
      color: macroCategoryColorMap[type],
      percentage: 0,
    })),
};

export const getMacroCategorySummaryPercentage = (
  value: number,
  total = 0
): number => {
  const percentage = total ? value / total : 0;
  return Math.round(percentage * 10000) / 10000;
};
